/**
 * Live Trading V3 — Spacebar Pull All, scoped to the FOCUSED GAME.
 *
 * One keyboard action that removes the trader's resting Kalshi exposure on
 * the game they are aimed at — and nothing else. The dangerous half of that
 * sentence is "and nothing else", so this module deliberately writes no new
 * cancellation logic at all. It is a keyboard entry point into the exact
 * machinery every mouse sweep already uses:
 *
 *   selectExactGameOrders  — the #272 exact-game selection: an order is a
 *     target only when its market resolves through marketStore's fail-closed
 *     canonical-key resolver to the SAME five-part game key. Reads the
 *     authoritative open-order state, never DOM rows, so off-screen and
 *     collapsed orders are included by construction. Anything that ties to
 *     the matchup but cannot be assigned to the exact game BLOCKS the whole
 *     action — cancelling "the easy subset" silently leaves exposure the
 *     trader believes is gone.
 *   freezeCancelSnapshot   — the immutable set the DELETEs are sent from.
 *   executeBulkOrderCancel — the ONE mixed-venue executor, with the #241
 *     cancel fence, per-order results, and retry-failed-only semantics.
 *
 * Boundaries in this release:
 *   • KALSHI ONLY — the hotkey lane is Kalshi-only everywhere else, and the
 *     pull is no wider than the keys that build the exposure. Polymarket
 *     resting orders on the game are NOT touched, and the summary does not
 *     pretend otherwise (the mouse "Cancel this game" sweep remains the
 *     mixed-venue tool).
 *   • Resting orders only. Positions are never flattened.
 *   • One pull at a time: a second Spacebar while one is pending refuses
 *     rather than duplicating cancellation traffic.
 *   • Every pull is AUTHORIZED server-side before any cancel is sent: the
 *     live_pull_all attempt record (game scope, exact frozen targets) must
 *     be durable first, and the relay refuses it when the LT3_PULL_ALL kill
 *     switch is off or the trader's LT2 grant is revoked. A failed or
 *     timed-out authorization refuses the WHOLE pull — fail closed, unlike
 *     the mouse sweeps' best-effort breadcrumb — because this is the
 *     packet's separate global kill boundary for the one-key action.
 *     Speed-demon D2 (260909): authorization + fan-out are ONE relay call —
 *     `POST /orders/cancel-sequence` with `surface:'live_pull_all'` and the
 *     frozen `targets` (via `accountStore.cancelOrdersByTickers`). The relay
 *     writes the attempt row and awaits it BEFORE any venue write, and its
 *     non-2xx means nothing was sent (`PullAllRefusedError` → `refused`).
 *     On a relay that predates the merged route (404) or gives no answer,
 *     the store falls back to the standalone authorizer + per-id DELETEs —
 *     the same kill boundary, two round trips, exactly the old path.
 */
import { useAccountStore } from '@/stores/accountStore';
import { useDisplaySettings } from '@/stores/displaySettings';
import {
  selectExactGameOrders,
  freezeCancelSnapshot,
} from '@/lib/trading/gameOrderSelection';
import { executeBulkOrderCancel } from '@/lib/trading/bulkOrderCancel';
import { PullAllRefusedError, recordPullAllAttempt } from '@/lib/trading/bulkCancelAttemptAudit';
import type { GameRow } from '@/views/ValueDashboard/types';
import type { KalshiOrder } from '@/types/kalshi';

export interface PullAllOutcome {
  status: 'refused' | 'empty' | 'done';
  /** One ack-line sentence — the strip is the trader's only receipt. */
  message: string;
  requested: number;
  submitted: number;
  failed: number;
}

let _pullInFlight = false;

export function isPullAllPending(): boolean {
  return _pullInFlight;
}

export function _resetPullAllForTests(): void {
  _pullInFlight = false;
}

/**
 * `onlyOrders`: sweep exactly these orders instead of the account's open
 * orders, still only where each provably belongs to `game` (the same exact
 * selection by ticker, the same server authorization, the same cancel path).
 * The hotkey listener's second pass uses it for the orders a press reported
 * as accepted after the first pass. They are named by the press itself,
 * because an order the venue just accepted is not in open orders yet (the V2
 * create response carries no status, so it appears only on the next refresh);
 * and an order the trader placed AFTER pressing Pull All is never swept.
 */
export async function executeFocusedGamePull(
  game: GameRow,
  opts: { onlyOrders?: ReadonlyArray<{ orderId: string; ticker: string }> } = {},
): Promise<PullAllOutcome> {
  if (_pullInFlight) {
    return {
      status: 'refused',
      message: 'Pull All already pending — wait for the result',
      requested: 0,
      submitted: 0,
      failed: 0,
    };
  }

  const account = useAccountStore.getState();
  const selection = selectExactGameOrders({
    game,
    kalshiOrders:
      opts.onlyOrders != null
        ? opts.onlyOrders.map((o) => ({ orderId: o.orderId, ticker: o.ticker }) as KalshiOrder)
        : account.openOrders ?? [],
    // Kalshi-only in this release — see the module contract above.
    polyOrders: [],
    tickerFallback: useDisplaySettings.getState().gameCancelTickerFallback,
  });

  if (selection.blockers.length > 0) {
    // Fail closed on the WHOLE action: a partially attributable game must
    // not be swept partially. The blocker messages name the orders.
    return {
      status: 'refused',
      message: `Pull refused — ${selection.blockers.length} order(s) could not be safely assigned to ${selection.gameLabel}`,
      requested: 0,
      submitted: 0,
      failed: 0,
    };
  }

  if (selection.targets.length === 0) {
    return {
      status: 'empty',
      message: `No resting Kalshi orders on ${selection.gameLabel}`,
      requested: 0,
      submitted: 0,
      failed: 0,
    };
  }

  // The relay scopes the pull by TICKER and honours the frozen ids inside
  // it; the tickers come from the same authoritative order state the
  // selection read. A target whose ticker cannot be recovered here would
  // silently drop out of the relay's scope — refuse the whole pull instead.
  const tickerByOrderId = new Map<string, string>();
  for (const o of account.openOrders ?? []) {
    if (typeof o.orderId === 'string' && typeof o.ticker === 'string' && o.ticker) {
      tickerByOrderId.set(o.orderId, o.ticker);
    }
  }
  const tickers = new Set<string>();
  for (const t of selection.targets) {
    const ticker = tickerByOrderId.get(t.orderId);
    if (!ticker) {
      return {
        status: 'refused',
        message: `Pull refused — order ${t.orderId} has no market ticker in open-order state`,
        requested: 0,
        submitted: 0,
        failed: 0,
      };
    }
    tickers.add(ticker);
  }

  _pullInFlight = true;
  try {
    const snapshot = freezeCancelSnapshot({
      scope: 'game',
      label: selection.gameLabel,
      targets: selection.targets,
    });
    const attemptId = crypto.randomUUID();
    // SERVER AUTHORIZATION + fan-out in ONE call — the kill boundary now
    // lives in the relay's cancel-sequence route: the live_pull_all attempt
    // row is awaited before any venue write, and a refusal (403 from the
    // kill switch or a revoked grant, 503 from an unwritable row) comes
    // back as PullAllRefusedError with nothing sent. Captured here so the
    // executor's per-order "failed" bookkeeping does not mask a refusal.
    let refusal: PullAllRefusedError | null = null;
    const report = await executeBulkOrderCancel(snapshot, {
      cancelKalshiOrders: async (ids) => {
        try {
          return await account.cancelOrdersByTickers([...tickers], {
            surface: 'live_pull_all',
            attemptId,
            snapshotId: snapshot.id,
            targets: ids,
            refreshAfter: false,
            // Old relay (404) / no answer: re-establish the boundary through
            // the standalone authorizer before the per-id DELETE fallback.
            authorizeFallback: () => recordPullAllAttempt(snapshot, attemptId),
          });
        } catch (err) {
          if (err instanceof PullAllRefusedError) refusal = err;
          throw err;
        }
      },
      // No Polymarket targets can exist in this snapshot (see above); the
      // executor still requires the dep, and reaching it would be a bug.
      requestPolymarketCancel: () => {
        throw new Error('Pull All is Kalshi-only');
      },
      refreshKalshi: () => account.refreshOrders(),
      refreshPolymarket: async () => {},
      // The relay records the attempt inside the merged call.
    });
    if (refusal !== null) {
      const message = (refusal as PullAllRefusedError).message;
      return {
        status: 'refused',
        message: `Pull refused — ${message || 'relay did not authorize the pull'}`,
        requested: 0,
        submitted: 0,
        failed: 0,
      };
    }

    const failed = report.failed.length + report.alreadyPending.length;
    const parts = [
      `pulled ${report.submitted.length}/${snapshot.targets.length}`,
      ...(report.failed.length > 0 ? [`${report.failed.length} FAILED`] : []),
      ...(report.alreadyPending.length > 0
        ? [`${report.alreadyPending.length} already cancelling`]
        : []),
      ...(report.refreshErrors.length > 0 ? ['refresh degraded'] : []),
      ...(report.auditError != null ? ['audit degraded'] : []),
    ];
    if (selection.tickerFallbackTargets > 0) {
      parts.push(`${selection.tickerFallbackTargets} identified by Kalshi ticker`);
    }
    return {
      status: 'done',
      message: `${selection.gameLabel}: ${parts.join(', ')}`,
      requested: snapshot.targets.length,
      submitted: report.submitted.length,
      failed,
    };
  } finally {
    _pullInFlight = false;
  }
}
